import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTicketTatComplianceReport,
  exportTicketTatComplianceReport,
  clearError,
} from 'store/slices/reportSlice';

import PageHeader from 'components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Clock, AlertTriangle, User, Filter, X, ChevronDown, ChevronUp, Ticket, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import LogDateFilter from 'components/common/LogDateFilter';
import DataTable from 'components/common/DataTable';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { fetchEmployeeDropdown } from 'store/slices/commonSlice';
import FormField from 'components/common/FormField';
import { formatDateTime } from 'utlis/formateDateTime';

const TicketTatComplianceReportPage = () => {
  const dispatch = useDispatch();

  const {
    ticketTatComplianceReport = { data: { summary: {}, results: [] }, pagination: null },
    isLoading,
    error,
  } = useSelector((state) => state.report);

  const summary = ticketTatComplianceReport.data.summary || {};
  const results = ticketTatComplianceReport.data.results || [];
  const pagination = ticketTatComplianceReport.pagination || {};

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilters, setDateFilters] = useState({});
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] = useState(false);

  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    status: '',
    ticket_code: '',
    assigned_to: '',
    created_by_department: '',
    created_by_name: '',
    issue_type: '',
    priority: '',
    tat_min: '',
    tat_max: '',
    period_start: '',
    period_end: '',
  });

  // Calculate total pages from server response with fallback
  const totalPages = pagination.total_pages || 1;

  // Check if any advanced filters are active (excluding 'all' values)
  const hasActiveAdvancedFilters = Object.values(advancedFilters).some(value =>
    value !== '' && value !== 'all'
  );

  const refetch = useCallback(() => {
    // Build the API params object with all filters
    const apiParams = {
      page,
      page_size: pageSize,
      search: searchTerm,
      report_type: 'tat_compliance',
      ...dateFilters,
      // Add advanced filters with proper naming for the backend
      ...(advancedFilters.status !== 'all' && { status: advancedFilters.status }),
      ...(advancedFilters.ticket_code && { ticket_code: advancedFilters.ticket_code }),
      ...(advancedFilters.assigned_to && { assigned_to: advancedFilters.assigned_to }),
      ...(advancedFilters.created_by_department && { created_by_department: advancedFilters.created_by_department }),
      ...(advancedFilters.created_by_name && { created_by_name: advancedFilters.created_by_name }),
      ...(advancedFilters.issue_type && { issue_type: advancedFilters.issue_type }),
      ...(advancedFilters.priority && { priority: advancedFilters.priority }),
      ...(advancedFilters.tat_min && { tat_min: advancedFilters.tat_min }),
      ...(advancedFilters.tat_max && { tat_max: advancedFilters.tat_max }),
      ...(advancedFilters.period_start && { period_start: advancedFilters.period_start }),
      ...(advancedFilters.period_end && { period_end: advancedFilters.period_end }),
    };


    dispatch(fetchTicketTatComplianceReport(apiParams));
  }, [dispatch, page, pageSize, searchTerm, dateFilters, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Reset to page 1 when search term, page size, or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, dateFilters, advancedFilters]);

  const employees = useSelector((state) => state.common?.employees?.items || []);

  const employeesLoading = useSelector((state) => state.common?.employees?.loading || false);

  // Fetch all employees on component mount
  useEffect(() => {
    dispatch(fetchEmployeeDropdown({}));
  }, [dispatch]);

  // --- Event Handlers ---
  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      status: '',
      ticket_code: '',
      assigned_to: '',
      created_by_department: '',
      created_by_name: '',
      issue_type: '',
      priority: '',
      tat_min: '',
      tat_max: '',
      period_start: '',
      period_end: '',
    });
  };

  const columns = [
    { accessorKey: 'ticket_code', header: 'Ticket Code' },
    { accessorKey: 'title', header: 'Title' },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.status_display;
        let variant = 'secondary';
        if (status === 'open') variant = 'default';
        if (status === 'in_progress') variant = 'default';
        if (status === 'closed') variant = 'outline';
        if (status === 'escalated') variant = 'destructive';
        return (
          <Badge variant={variant}>
            {status}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'tat',
      header: 'TAT (Turn Around Time)',
      cell: ({ row }) => {
        const tat = row.original.tat || 'N/A';
        return (
          <Badge variant={tat === 'N/A' ? 'outline' : 'default'}>
            {tat}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }) => formatDateTime(row.original.created_at) ?? '-'
    },
    {
      accessorKey: 'assigned_to_name',
      header: 'Assigned To',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          <span>{row.original.assigned_to_name}</span>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="TAT Compliance Report"
        description="Analyze ticket resolution times and compliance with service level agreements"
      />

      <LogDateFilter onFilter={(f) => setDateFilters(f)} />

      {/* Advanced Filters Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
              {hasActiveAdvancedFilters && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveAdvancedFilters && (
                <Button variant="outline" size="sm" onClick={resetAdvancedFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)}
              >
                {isAdvancedFilterExpanded ? (
                  <>
                    Hide <ChevronUp className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {isAdvancedFilterExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

              <FormField
                label="Status"
                name="status"
                type="select"
                value={advancedFilters.status}
                onChange={(e) => handleAdvancedFilterChange('status', e.target.value)}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'open', label: 'Open' },
                  { value: 'in_progress', label: 'In Progress' },
                  { value: 'closed', label: 'Closed' },
                  { value: 'escalated', label: 'Escalated' },
                  { value: 'ack_pending', label: 'Awaiting Acknowledgement' },
                ]}
              />

              <FormField
                label="Assigned To"
                name="assigned_to"
                type="select"
                value={advancedFilters.assigned_to}
                onChange={(e) => handleAdvancedFilterChange('assigned_to', e.target.value)}
                disabled={employeesLoading}
                options={[
                  ...(Array.isArray(employees)
                    ? employees.map((employee) => ({
                      value: String(employee.id),
                      label: employee.name,
                    }))
                    : []),
                ]}
                placeholder={employeesLoading ? "Loading..." : "Select employee"}
              />


              {/* <div className="space-y-2">
                <Label htmlFor="created_by_department">Created By Department</Label>
                <Select
                  value={advancedFilters.created_by_department}
                  onValueChange={(value) => handleAdvancedFilterChange('created_by_department', value)}
                  disabled={employeesLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={employeesLoading ? "Loading..." : "Select department"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Array.isArray(employees) && employees.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div> */}

              {/* <div className="space-y-2">
                <Label htmlFor="created_by_name">Created By Name</Label>
                <Select
                  value={advancedFilters.created_by_name}
                  onValueChange={(value) => handleAdvancedFilterChange('created_by_name', value)}
                  disabled={employeesLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={employeesLoading ? "Loading..." : "Select employee"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Array.isArray(employees) && employees.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div> */}

              {/* <div className="space-y-2">
                <Label htmlFor="issue_type">Issue Type</Label>
                <Select
                  value={advancedFilters.issue_type}
                  onValueChange={(value) => handleAdvancedFilterChange('issue_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select issue type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="bug">Bug</SelectItem>
                    <SelectItem value="feature">Feature Request</SelectItem>
                    <SelectItem value="enhancement">Enhancement</SelectItem>
                    <SelectItem value="question">Question</SelectItem>
                    <SelectItem value="incident">Incident</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}

              {/* <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={advancedFilters.priority}
                  onValueChange={(value) => handleAdvancedFilterChange('priority', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}

              <div className="space-y-2">
                <Label>TAT Range (Hours)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    {/* <Label htmlFor="tat_min">Min TAT</Label> */}
                    <Input
                      type="number"
                      placeholder="Min hours"
                      value={advancedFilters.tat_min}
                      onChange={(e) => handleAdvancedFilterChange('tat_min', e.target.value)}
                    />
                  </div>
                  <div>
                    {/* <Label htmlFor="tat_max">Max TAT</Label> */}
                    <Input
                      type="number"
                      placeholder="Max hours"
                      value={advancedFilters.tat_max}
                      onChange={(e) => handleAdvancedFilterChange('tat_max', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tickets</CardTitle>
            <Ticket className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pagination.count || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Compliant</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {summary.compliant || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Non-Compliant</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {summary.non_compliant || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Compliance Rate</CardTitle>
            <Clock className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {summary.compliance_rate || '0%'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Resolution Time</CardTitle>
            <Clock className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {summary.average_resolution_time?.replace(/\.\d+$/, '') || 'N/A'}
            </div>

          </CardContent>
        </Card>
      </div>

      {isLoading && results.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin mr-3" />
          <span className="text-lg">Loading TAT compliance data...</span>
        </div>
      ) : (
        <DataTable
          data={results} // Use the paginated results from the server
          columns={columns}
          totalCount={pagination.count}
          searchPlaceholder="Search by ticket code, title..."
          onSearch={setSearchTerm}
          showSearch={true}
          pageIndex={page - 1} // Convert to 0-based index for DataTable
          pageSize={pageSize}
          pageCount={totalPages} // Use total pages from server response
          onPageChange={(p) => setPage(p + 1)} // Convert back to 1-based index
          onPageSizeChange={(newSize) => setPageSize(newSize)}
          isLoading={isLoading}
          onExport={() =>
            dispatch(exportTicketTatComplianceReport({
              filters: {
                page,
                page_size: pageSize,
                search: searchTerm,
                report_type: 'tat_compliance',
                ...dateFilters,
                // Include advanced filters in export
                ...(advancedFilters.status !== 'all' && { status: advancedFilters.status }),
                ...(advancedFilters.ticket_code && { ticket_code: advancedFilters.ticket_code }),
                ...(advancedFilters.assigned_to && { assigned_to: advancedFilters.assigned_to }),
                ...(advancedFilters.created_by_department && { created_by_department: advancedFilters.created_by_department }),
                ...(advancedFilters.created_by_name && { created_by_name: advancedFilters.created_by_name }),
                ...(advancedFilters.issue_type && { issue_type: advancedFilters.issue_type }),
                ...(advancedFilters.priority && { priority: advancedFilters.priority }),
                ...(advancedFilters.tat_min && { tat_min: advancedFilters.tat_min }),
                ...(advancedFilters.tat_max && { tat_max: advancedFilters.tat_max }),
                ...(advancedFilters.period_start && { period_start: advancedFilters.period_start }),
                ...(advancedFilters.period_end && { period_end: advancedFilters.period_end }),
              }
            }))
          }
          showExport={true}
          showImport={false}
        />
      )}
    </div>
  );
};

export default TicketTatComplianceReportPage;